// Shared country-code + phone helpers used by the Add / Edit customer forms.
// The national number stays 10 digits (validated everywhere); the country code
// is captured via a dropdown and combined into the stored value as
// "<code> <digits>", e.g. "+1 5551234567".

export interface CountryCode {
  code: string;
  /** ISO-ish country abbreviation, shown in the dropdown list (e.g. "IN"). */
  country: string;
}

// Dial codes for the countries the app already lists in its timezone/locale
// selectors, plus a few common ones. The dropdown list shows "<country> <code>"
// (e.g. "IN +91"); the collapsed control shows just the code (e.g. "+91").
export const COUNTRY_CODES: CountryCode[] = [
  { code: '+1', country: 'US' },
  { code: '+44', country: 'GB' },
  { code: '+91', country: 'IN' },
  { code: '+61', country: 'AU' },
  { code: '+49', country: 'DE' },
  { code: '+33', country: 'FR' },
  { code: '+39', country: 'IT' },
  { code: '+55', country: 'BR' },
  { code: '+81', country: 'JP' },
  { code: '+86', country: 'CN' },
];

/** Shown when a number needs a country code and none has been picked. */
export const COUNTRY_CODE_REQUIRED_MESSAGE = 'Select a country code';

/**
 * The number to reach a ticket's contact on: the one saved on the ticket first,
 * then the customer's, then the places older tickets kept it.
 */
export function contactPhoneOf(ticket: any): string | undefined {
  const customFields = ticket.customFields || {};
  const metadata = ticket.metadata || {};
  return (
    ticket.contactPhone ||
    ticket.customer?.phone ||
    customFields.phoneNumber ||
    customFields.phone ||
    customFields.contact_number ||
    metadata.phoneNumber ||
    metadata.phone ||
    undefined
  );
}

/** Whether a stored number says which country it is in. */
export function hasCountryCode(phone?: string | null): boolean {
  return (phone || '').trim().startsWith('+');
}

/** Combine a dial code + national digits into the stored phone string. */
export function combinePhone(countryCode: string, national: string): string {
  const digits = (national || '').replace(/\D/g, '');
  if (!digits) return '';
  return `${countryCode} ${digits}`;
}

/**
 * Split a stored phone value back into { countryCode, national }. There is no
 * default country: a number with no code we recognise, like Exotel's
 * "09172549693", comes back with an empty countryCode for someone to pick.
 * `national` is the last 10 digits.
 */
export function parsePhone(phone?: string | null): { countryCode: string; national: string } {
  const trimmed = (phone || '').trim();
  if (trimmed.startsWith('+')) {
    const compact = trimmed.replace(/[^\d+]/g, '');
    const match = COUNTRY_CODES.find((c) => compact.startsWith(c.code));
    if (match) return { countryCode: match.code, national: compact.slice(match.code.length).slice(-10) };
  }
  return { countryCode: '', national: trimmed.replace(/\D/g, '').slice(-10) };
}

/**
 * The phone value to save from an edit form. An untouched number goes back
 * exactly as stored: parsePhone keeps only the last 10 digits and drops codes
 * it doesn't recognise, so rebuilding it would change a number nobody edited.
 */
export function phoneToSave(stored: string | null | undefined, countryCode: string, national: string): string {
  const before = parsePhone(stored);
  if (before.countryCode === countryCode && before.national === national) return stored || '';
  return combinePhone(countryCode, national);
}

// ── Free-form phone inputs (custom fields, provider numbers, portal profile) ──
// Inputs that don't use the country-code dropdown above. type="tel" does no
// validation in any browser, so without these a value like "abc" was saved.

/** Characters a typed phone number may contain: digits, a leading +, and the
 *  usual separators. */
const PHONE_CHARS_RE = /^\+?[\d\s().-]+$/;

export const PHONE_INVALID_MESSAGE = 'Enter a valid phone number (7–15 digits, e.g. +91 98765 43210)';
export const E164_INVALID_MESSAGE = 'Enter the number in international format, e.g. +14155552671';

/** Drop anything that can't be part of a phone number, so letters never land
 *  in the field. Keeps a + only as the first character. */
export function sanitizePhoneInput(value: string): string {
  const cleaned = (value || '').replace(/[^\d\s().+-]/g, '');
  return cleaned.replace(/(?!^)\+/g, '');
}

/** Lenient check: separators allowed, 7–15 digits (the E.164 maximum). */
export function isValidPhoneNumber(value: string): boolean {
  const v = (value || '').trim();
  if (!PHONE_CHARS_RE.test(v)) return false;
  const digits = v.replace(/\D/g, '').length;
  return digits >= 7 && digits <= 15;
}

/** Strict E.164 (what Twilio requires): +, country code, no separators. */
export function isValidE164(value: string): boolean {
  return /^\+[1-9]\d{6,14}$/.test((value || '').trim());
}
